import React from 'react';
import PropTypes from 'prop-types';
import { IcoActivity, IcoBolt, IcoSpark } from '../bz/icons';

const Pillar = ({ Icon, title, body }) => (
  <div className="bz-card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        background: 'var(--predict-soft)',
        color: 'var(--predict)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={17} />
    </div>
    <div className="bz-h3">{title}</div>
    <div className="bz-muted" style={{ fontSize: 'var(--fs-md)', lineHeight: 1.5 }}>
      {body}
    </div>
  </div>
);
Pillar.propTypes = { Icon: PropTypes.elementType, title: PropTypes.string, body: PropTypes.string };

const PredictComingSoon = () => (
  <div
    style={{
      padding: '32px 32px 24px',
      display: 'flex',
      flexDirection: 'column',
      gap: 28,
      maxWidth: 1320,
      margin: '0 auto'
    }}
  >
    <div
      style={{
        background: 'linear-gradient(180deg, var(--predict-soft) 0%, var(--bg-soft) 100%)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-lg)',
        padding: '40px 40px',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <svg
        viewBox="0 0 400 400"
        style={{ position: 'absolute', right: -80, top: -80, width: 380, height: 380, opacity: 0.25 }}
      >
        <g stroke="var(--predict)" strokeWidth="1" fill="none">
          <ellipse cx="200" cy="200" rx="180" ry="60" transform="rotate(-20 200 200)" />
          <ellipse cx="200" cy="200" rx="140" ry="48" transform="rotate(15 200 200)" />
          <ellipse cx="200" cy="200" rx="100" ry="34" transform="rotate(45 200 200)" />
          <circle cx="200" cy="200" r="6" fill="var(--predict)" />
          <circle cx="350" cy="170" r="3" fill="var(--predict)" />
          <circle cx="100" cy="240" r="3" fill="var(--predict)" />
        </g>
      </svg>

      <div style={{ position: 'relative', maxWidth: 640 }}>
        <span className="bz-pill bz-pill--predict" style={{ marginBottom: 16 }}>
          <span className="bz-dot" /> Coming Q3 2026 · Early access open
        </span>
        <h1 className="bz-h1" style={{ fontSize: 'var(--fs-4xl)', marginBottom: 14, lineHeight: 1.1 }}>
          See the CVE before it&rsquo;s a CVE.
        </h1>
        <p
          style={{
            margin: 0,
            color: 'var(--ink-soft)',
            fontSize: 'var(--fs-lg)',
            lineHeight: 1.55,
            maxWidth: 560
          }}
        >
          BugZero <strong>Predict</strong> models the operational bug feeds from your vendors and forecasts which
          defects are likely to become disclosed CVEs — giving your security team a 14–90 day head start on patch
          planning.
        </p>
      </div>
    </div>

    <div>
      <div className="bz-kicker" style={{ color: 'var(--predict)', marginBottom: 12 }}>
        What it does
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        <Pillar
          Icon={IcoActivity}
          title="Bug-to-CVE probability"
          body="Every operational bug in your inventory gets a 0–100 score for the likelihood it becomes a published CVE in the next 90 days."
        />
        <Pillar
          Icon={IcoSpark}
          title="Vendor trend forecasting"
          body="Surface vendor families and product lines where disclosure cadence is accelerating, so you can pre-empt waves of advisories."
        />
        <Pillar
          Icon={IcoBolt}
          title="Pre-disclosure routing"
          body="High-probability bugs are pushed to ServiceNow or Splunk before the upstream advisory drops — close the gap to zero."
        />
      </div>
    </div>

    <div
      className="bz-card"
      style={{ padding: 20, display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 24, alignItems: 'center' }}
    >
      <div>
        <div className="bz-kicker" style={{ color: 'var(--predict)', marginBottom: 6 }}>
          How it works
        </div>
        <h3 className="bz-h3" style={{ marginBottom: 8 }}>
          Signal-rich, vendor-aware
        </h3>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 'var(--fs-md)', lineHeight: 1.55 }}>
          Predict combines the bug bulletins you already see in Prevent with vendor-specific disclosure history,
          shared-code signals across products, and historical bug→CVE pairings — then validates against the last 36
          months of disclosed CVEs.
        </p>
      </div>
      <div
        style={{
          background: 'var(--bg-soft)',
          border: '1px solid var(--line-soft)',
          borderRadius: 8,
          padding: 16
        }}
      >
        <div className="bz-row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
          <span className="bz-kicker">Prediction confidence · last 36 mo backtest</span>
          <span className="bz-pill bz-pill--predict bz-mono">AUC 0.87</span>
        </div>
        <svg viewBox="0 0 400 110" style={{ width: '100%', height: 110 }}>
          <defs>
            <linearGradient id="predGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--predict)" stopOpacity="0.35" />
              <stop offset="100%" stopColor="var(--predict)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M0,90 C40,70 80,55 120,60 C160,65 200,40 240,35 C280,30 320,25 360,18 L400,15 L400,110 L0,110 Z"
            fill="url(#predGrad)"
          />
          <path
            d="M0,90 C40,70 80,55 120,60 C160,65 200,40 240,35 C280,30 320,25 360,18 L400,15"
            stroke="var(--predict)"
            strokeWidth="1.8"
            fill="none"
          />
          {[40, 110, 180, 250, 320].map((x, i) => (
            <circle key={i} cx={x} cy={[78, 60, 55, 38, 25][i]} r="2.5" fill="var(--predict)" />
          ))}
        </svg>
      </div>
    </div>
  </div>
);

export default PredictComingSoon;
